import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import {
  ChartConfig,
  configureXAxisRendererTicks,
  configureYAxisRendererTicks,
  createChartLegend,
  createTooltip,
  customizeAxisLabels,
  DowntimeEntry,
  prepareChartContainer,
  renderDowntimeAndHighlightEvents,
  setupCursor,
  setupCursorEvents,
} from './chartHelpers';
import { generateLineType, generateMarkers } from './chartUtils';
import { ChartViewOptions } from '../components/slices/AssetTrendDataSlice';

interface DataSet {
  title: string;
  shortUnitOfMeasure: string;
  dataPoints: { date: number; value: number }[];
}

export const createDualYAxisChart = (
  container: HTMLElement,
  chartData: DataSet[],
  parameterData: ChartConfig[] = [],
  chartViewOptions: ChartViewOptions,
  downtimeData?: DowntimeEntry[],
  pumpCheckerEventsSlice?: any[],
  startDate?: Date,
  endDate?: Date,
) => {
  if (!prepareChartContainer(container)) {
    return;
  }

  const root = am5.Root.new(container);
  root.setThemes([am5themes_Dark.new(root)]);
  const { chartType, showTooltip } = chartViewOptions;

  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      width: am5.percent(95),
      layout: root.verticalLayout,
    }),
  );

  // X Axis
  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      maxDeviation: 0.2,
      baseInterval: { timeUnit: 'second', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
        minGridDistance: 100,
        minorGridEnabled: true,
        pan: 'zoom',
      }),
    }),
  );

  if (startDate && endDate) {
    xAxis.setAll({
      min: new Date(startDate).getTime(),
      max: new Date(endDate).getTime(),
      strictMinMax: true,
    });
  }

  const xAxisRenderer = xAxis.get('renderer') as am5xy.AxisRendererX; // Explicitly cast to AxisRendererX
  configureXAxisRendererTicks(xAxisRenderer);

  // Primary Y Axis — Pressure (except Tubing Pressure)
  const yAxisLeft = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      maxDeviation: 0.2,
      renderer: am5xy.AxisRendererY.new(root, {
        pan: 'zoom',
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
      }),
    }),
  );

  const belongsToLeftAxis = (title: string) => {
    const lower = title.toLowerCase();
    return lower.includes('pressure') && !lower.includes('tubing pressure') && !lower.includes('tubing head pressure');
  };

  const leftAxisSeries = chartData.filter(
    (d) => belongsToLeftAxis(d.title) && Array.isArray(d.dataPoints) && d.dataPoints.length > 0,
  );
  const yAxisLeftLabels = Array.from(new Set(leftAxisSeries.map((d) => `${d.title} (${d.shortUnitOfMeasure})`))).join(
    ', ',
  );

  if (leftAxisSeries.length > 0) {
    yAxisLeft.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: yAxisLeftLabels,
        y: am5.percent(50),
        centerX: am5.percent(50),
        textAlign: 'center',
        oversizedBehavior: 'wrap',
        maxHeight: 500,
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
        marginRight: -5,
        minWidth: 45,
      }),
    );
  }

  const yAxisLeftRenderer = yAxisLeft.get('renderer') as am5xy.AxisRendererY;
  configureYAxisRendererTicks(yAxisLeftRenderer);

  yAxisLeft.get('renderer').ticks.template.adapters.add('x', function () {
    return yAxisLeft.width() - 40;
  });

  // Secondary Y Axis — Tubing Pressure + others
  const yAxisRight = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, { opposite: true }),
    }),
  );

  const rightAxisSeries = chartData.filter(
    (d) => !belongsToLeftAxis(d.title) && Array.isArray(d.dataPoints) && d.dataPoints.length > 0,
  );

  const yAxisRightLabels = Array.from(new Set(rightAxisSeries.map((d) => `${d.title} (${d.shortUnitOfMeasure})`))).join(
    ', ',
  );

  if (rightAxisSeries.length > 0) {
    yAxisRight.children.push(
      am5.Label.new(root, {
        rotation: 90,
        text: yAxisRightLabels,
        y: am5.p50,
        centerX: am5.p50,
        marginRight: -120,
        textAlign: 'center',
        oversizedBehavior: 'wrap',
        maxHeight: 500,
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
      }),
    );
  }

  const yAxisRightRenderer = yAxisRight.get('renderer') as am5xy.AxisRendererY;
  configureYAxisRendererTicks(yAxisRightRenderer, { minPosition: 0 });

  customizeAxisLabels(yAxisLeft.get('renderer'), 14);
  customizeAxisLabels(yAxisRight.get('renderer'), 14);

  // Store series and bullets
  const allSeries: am5xy.LineSeries[] = [];
  const bullets: am5.Bullet[] = [];

  // Add legend
  const legend = createChartLegend(chart, root);

  // Function to create cursor bullet for a series
  const createCursorBullet = (series: am5xy.LineSeries, markerColor: string | undefined) => {
    const bullet = am5.Bullet.new(root, {
      sprite: am5.Circle.new(root, {
        radius: 6,
        fill: am5.color('#323F46'),
        stroke: am5.color(markerColor || '#60BFDA'),
        strokeWidth: 4,
        visible: false,
      }),
    });
    series.bulletsContainer.children.push(bullet.get('sprite'));
    return bullet;
  };

  chartData.forEach((dataset) => {
    const matchedParam = parameterData.find((param) => param.name.toLowerCase() === dataset.title.toLowerCase());
    const lineColor = matchedParam?.lineColor || '#0000FF';
    const lineType = matchedParam?.lineType || 'solid';
    const lineWeight = matchedParam?.lineWeight || 2;
    const markerType = matchedParam?.markerType || 'circle';
    const markerColor = matchedParam?.markerColor || lineColor;
    const markerSize = matchedParam?.markerSize || 2;

    const isLeftAxis = belongsToLeftAxis(dataset.title);
    const yAxis = isLeftAxis ? yAxisLeft : yAxisRight;

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: chartType === 'scatter' ? 'Scatter chart' : 'Line chart',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        stroke: am5.color(lineColor),
      }),
    );

    generateLineType(series, lineType, lineWeight);

    if (chartType === 'scatter') {
      series.strokes.template.setAll({
        strokeOpacity: 0,
      });
      // Add custom marker bullet
      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: generateMarkers(markerType, markerColor, markerSize, root),
        });
      });
    }

    // Tooltip and data
    const tooltip = createTooltip('', root, {
      backgroundColor: '#001023',
      backgroundOpacity: 1,
      strokeColor: '#001023',
      textColor: '#FFFFFF',
      fontSize: 14,
      verticalPosition: 'center',
      chartHeight: chart.plotContainer.height(),
    });

    if (showTooltip) {
      series.set('tooltip', tooltip);
    }
    series.data.setAll(dataset.dataPoints);
    allSeries.push(series);

    // Add cursor bullet
    bullets.push(createCursorBullet(series, chartType === 'scatter' ? markerColor : lineColor));
    legend.data.push(series);
    series.set('legendLabelText', dataset.title);
    (series as any).setPrivate('legendMarkerFill', am5.color(chartType === 'scatter' ? markerColor : lineColor));
  });

  const cursor = setupCursor(chart, root, xAxis, '#FB743C', 3);
  setupCursorEvents(cursor, xAxis, chart, allSeries, bullets, chartData, parameterData, chartType);
  renderDowntimeAndHighlightEvents(
    root,
    xAxis,
    chartViewOptions,
    downtimeData,
    showTooltip,
    pumpCheckerEventsSlice,
    true,
  );

  return root;
};
